/** Team API types — mirror the mobile app's teamTypes/constants. */
import { brand } from '../theme/theme';

export type TeamRoleName = 'administrator' | 'admin' | 'provider' | 'supervisor';
export type AssignableTeamRole = 'admin' | 'provider' | 'supervisor';
export type TeamMemberStatus = 'invited' | 'active' | 'inactive' | 'removed';

export interface TeamMemberUser {
  phone_number: string;
  country_code: string;
  display_name: string | null;
  profile_image_url: string | null;
  status: 'active' | 'pending_verification' | 'inactive';
}

export interface TeamMember {
  id: string;
  business_id: string;
  user_id: string;
  role_id: string;
  role_name: TeamRoleName;
  role_display_name: string;
  is_creator: boolean;
  is_provider: boolean;
  status: TeamMemberStatus;
  invited_at: string;
  joined_at: string | null;
  user: TeamMemberUser;
}

export interface PaginationInfo {
  page: number; limit: number; total: number; total_pages: number; has_next: boolean; has_previous: boolean;
}

export interface CreateTeamMemberRequest {
  phone_number: string;
  country_code?: string;
  display_name?: string;
  role: AssignableTeamRole;
  is_provider?: boolean;
}

export interface UpdateTeamMemberRequest {
  display_name?: string;
  role?: AssignableTeamRole;
  is_provider?: boolean;
}

// ---- display config ----
export const ROLE_OPTIONS: { id: AssignableTeamRole; label: string; description: string }[] = [
  { id: 'admin', label: 'Admin', description: 'Full access to all settings and team management.' },
  { id: 'provider', label: 'Provider', description: 'Access to own profile & can create personal queues.' },
  { id: 'supervisor', label: 'Supervisor', description: 'Only queue-level access to start & update queues.' },
];

export const roleChipColor = (role: TeamRoleName): { bg: string; fg: string } => {
  switch (role) {
    case 'provider': return { bg: '#F5F3FF', fg: '#7C3AED' };
    case 'supervisor': return { bg: '#FFFBEB', fg: '#D97706' };
    default: return { bg: '#EFF6FF', fg: '#065CC6' }; // admin / administrator
  }
};

export const roleGradient = (role: TeamRoleName, deactivated?: boolean): string => {
  if (deactivated) return brand.gradDeactivated;
  if (role === 'provider') return brand.gradProvider;
  if (role === 'supervisor') return brand.gradSupervisor;
  return brand.gradAdmin;
};

export interface MemberDisplay { label: string; kind: 'active' | 'invited' | 'deactivated'; }
export const memberDisplayStatus = (s: TeamMemberStatus): MemberDisplay => {
  switch (s) {
    case 'active': return { label: 'Active', kind: 'active' };
    case 'invited': return { label: 'Invited', kind: 'invited' };
    default: return { label: 'Deactivated', kind: 'deactivated' }; // inactive | removed
  }
};

/** Member is currently active-ish (so the action is "Deactivate"). */
export const isMemberActive = (s: TeamMemberStatus) => s === 'active' || s === 'invited';
